import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EnergyDisplay, RewardModal, BaseLayout, MiniQuizModal } from '../components';
import { kilobaApi } from '../services/api';
import type { UserKiloba, MiniQuizQuestion, Voucher, KilobaStatus } from '../services/api';
import './KilobaHubPage.css';

// Component for animated arrows
const AnimatedArrow: React.FC<{ index: number }> = ({ index }) => {
  const [style, setStyle] = useState({});

  const generateRandomStyle = () => {
    // Generate random position and size for each arrow
    const randomLeft = Math.random() * 70 + 10; // 10% to 80%
    const randomBottom = Math.random() * 5 + 40; // 40% to 60%
    const randomHeight = Math.random() * 50 + 25; // 25px to 75px height (width auto để giữ tỉ lệ)
    const randomDelay = Math.random() * 5; // 0 to 5 seconds
    const randomDuration = Math.random() * 1 + 2.5; // 2.5 to 3.5 seconds

    return {
      left: `${randomLeft}%`,
      bottom: `${randomBottom}%`,
      height: `${randomHeight}px`,
      width: 'auto', // Giữ tỉ lệ ảnh
      animationDelay: `${randomDelay}s`,
      animationDuration: `${randomDuration}s`,
    };
  };

  useEffect(() => {
    // Set initial random style
    setStyle(generateRandomStyle());

    // Update random position every 3-4 seconds to create continuous randomness
    const interval = setInterval(() => {
      setStyle(generateRandomStyle());
    }, 3500 + Math.random() * 1000);

    return () => clearInterval(interval);
  }, [index]);

  return (
    <img 
      src="/images/kilohub/arrow_up.png" 
      alt="Upgrade Animation" 
      className="upgrade-arrow"
      style={style}
    />
  );
};

const KilobaHubPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [currentLevel, setCurrentLevel] = useState(0);
  const [maxLevel] = useState(100);
  const [universalKiloba, setUniversalKiloba] = useState<UserKiloba | null>(null);
  const [kilobaStatus, setKilobaStatus] = useState<KilobaStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentEnergy, setCurrentEnergy] = useState<number | null>(null);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  // Modal state
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [rewardData, setRewardData] = useState({
    title: '',
    body1: '',
    body2: '',
    image: '',
    footer: ''
  });

  // New state for reward system
  const [showMiniQuizModal, setShowMiniQuizModal] = useState(false);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState<MiniQuizQuestion | null>(null);
  const [pendingReward, setPendingReward] = useState<{
    type: 'basic' | 'miniQuiz' | 'voucher10k' | 'voucher20k';
    voucher?: Voucher;
    upgradeResult?: any;
  } | null>(null);
  const [showGiftModal, setShowGiftModal] = useState(false);

  // Load initial data when component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      if (!user?.id) return;
      
      try {
        // Sử dụng API mới để lấy trạng thái Kiloba đầy đủ
        const statusResponse = await kilobaApi.getKilobaStatus(user.id);
        if (statusResponse.success && statusResponse.data) {
          setKilobaStatus(statusResponse.data);
          setUniversalKiloba(statusResponse.data.universalKiloba);
          if (statusResponse.data.universalKiloba) {
            setCurrentLevel(statusResponse.data.universalKiloba.growthPoints);
          }
        }
      } catch (error) {
        console.error('Error loading Kiloba status:', error);
        // Fallback to old API if new one fails
        try {
          const response = await kilobaApi.getUserUniversalKiloba(user.id);
          if (response.success && response.data) {
            setUniversalKiloba(response.data);
            setCurrentLevel(response.data.growthPoints);
          }
        } catch (fallbackError) {
          console.error('Error loading Kiloba data with fallback:', fallbackError);
        }
      }
    };

    // Only load data when we have a user ID and haven't loaded data yet
    if (user?.id && !universalKiloba) {
      loadInitialData();
    }
    
    // Set initial energy from user object
    if (user?.energy !== undefined && currentEnergy === null) {
      setCurrentEnergy(user.energy);
    }
  }, [user?.id, user?.energy]); // Depend on user ID and energy

  const handleBack = () => {
    navigate('/');
  };

  const handleUpgrade = async () => {
    const energyToUse = currentEnergy ?? user?.energy ?? 0;
    
    if (loading || energyToUse < 1) {
      setRewardData({
        title: 'Không đủ Năng lượng!',
        body1: 'Bạn cần ít nhất 1 Năng lượng để thăng cấp Kiloba.',
        body2: 'Hãy tham gia các hoạt động để nhận thêm Năng lượng.',
        image: '',
        footer: 'Chúc bạn may mắn!'
      });
      setShowRewardModal(true);
      return;
    }

    setLoading(true);
    setIsUpgrading(true);
    
    try {
      const response = await kilobaApi.upgradeKiloba();
      
      if (response.success && response.data) {
        const { kiloba, pointsGained, matured, newSpecies, newEnergyTotal, rewardType, voucher, miniQuizQuestion } = response.data;
        
        // Update local state
        setUniversalKiloba(kiloba);
        setCurrentLevel(kiloba.growthPoints);
        setCurrentEnergy(newEnergyTotal);

        console.log('Upgrade successful:', {
          kiloba,
          pointsGained,
          matured,
          newSpecies,
          newEnergyTotal,
          rewardType,
          voucher,
          miniQuizQuestion
        });

        // Handle different reward types
        if (rewardType === 'voucher10k' || rewardType === 'voucher20k') {
          // Show gift modal for voucher rewards
          setPendingReward({ type: rewardType, voucher: voucher || undefined, upgradeResult: { kiloba, pointsGained, matured, newSpecies } });
          setRewardData({
            title: 'Chúc mừng!',
            body1: `Nhận được ${pointsGained} điểm trưởng thành!`,
            body2: 'Bạn còn nhận được một món quà đặc biệt!',
            image: '/images/kilohub/gift.png',
            footer: 'Nhấn vào quà để mở!'
          });
          setShowGiftModal(true);
        } else if (rewardType === 'miniQuiz' && miniQuizQuestion) {
          // Show gift modal for mini quiz
          setCurrentQuizQuestion(miniQuizQuestion);
          setPendingReward({ type: rewardType, upgradeResult: { kiloba, pointsGained, matured, newSpecies } });
          setRewardData({
            title: 'BINGO! Tham gia trả lời câu hỏi để x2 phần thưởng!',
            body1: `Nhận được ${pointsGained} điểm trưởng thành!`,
            body2: 'Bạn có cơ hội nhận thêm 1 điểm nữa!',
            image: '/images/kilohub/gift.png',
            footer: 'Nhấn vào quà để trả lời câu hỏi!'
          });
          setShowGiftModal(true);
        } else {
          // Basic reward or matured
          if (matured && newSpecies) {
            setRewardData({
              title: 'Chúc mừng!',
              body1: `Kiloba của bạn đã thăng cấp thành ${newSpecies.name}!`,
              body2: `Nhận được ${pointsGained} điểm trưởng thành. KILOBA trưởng thành đã được thêm vào Bộ sưu tập`,
              image: `/images/kilohub/${newSpecies.code.toLowerCase()}_rewards.png`,
              footer: 'Chúc mừng bạn đã có một Kiloba trưởng thành!'
            });
          } else {
            setRewardData({
              title: 'Nâng cấp thành công!',
              body1: `Nhận được ${pointsGained} điểm trưởng thành!`,
              body2: `Kiloba hiện có ${kiloba.growthPoints}/100 điểm. Còn ${100 - kiloba.growthPoints} điểm nữa để thăng cấp.`,
              image: '',
              footer: 'Tiếp tục cố gắng!'
            });
          }
          setShowRewardModal(true);
        }
      }
    } catch (error: any) {
      console.error('Error upgrading Kiloba:', error);
      
      setRewardData({
        title: 'Lỗi!',
        body1: 'Không thể thăng cấp Kiloba.',
        body2: 'Vui lòng thử lại sau.',
        image: '/images/icons/error.png',
        footer: 'Xin lỗi vì sự bất tiện!'
      });
      setShowRewardModal(true);
    } finally {
      setLoading(false);
      setIsUpgrading(false);
    }
  };

  // Handle gift modal image click
  const handleGiftClick = () => {
    setShowGiftModal(false);
    
    if (pendingReward?.type === 'miniQuiz' && currentQuizQuestion) {
      setShowMiniQuizModal(true);
    } else if (pendingReward?.type === 'voucher10k' || pendingReward?.type === 'voucher20k') {
      // Show voucher reward
      const voucherType = pendingReward.type === 'voucher10k' ? '10,000 VNĐ' : '20,000 VNĐ';
      setRewardData({
        title: 'Chúc mừng!',
        body1: `Bạn đã nhận được voucher ${voucherType}!`,
        body2: 'Voucher đã được thêm vào tài khoản của bạn. Hãy truy cập mục Thông báo để xem mã voucher.',
        image: '/images/kilohub/reward.png',
        footer: 'Hãy sử dụng voucher để mua sắm!'
      });
      setShowRewardModal(true);
      setPendingReward(null);
    }
  };

  // Handle mini quiz answer
  const handleMiniQuizAnswer = async (questionId: string, answer: boolean) => {
    try {
      const response = await kilobaApi.answerMiniQuiz(questionId, answer);
      
      if (response.success && response.data) {
        const { correct, bonusGranted } = response.data;
        
        setShowMiniQuizModal(false);
        
        if (correct && bonusGranted) {
          // Update kiloba growth points
          if (universalKiloba) {
            const updatedKiloba = { ...universalKiloba, growthPoints: universalKiloba.growthPoints + 1 };
            setUniversalKiloba(updatedKiloba);
            setCurrentLevel(updatedKiloba.growthPoints);
          }
          
          setRewardData({
            title: 'Chính xác!',
            body1: 'Bạn đã trả lời đúng câu hỏi!',
            body2: 'Nhận được thêm 1 điểm trưởng thành cho Kiloba.',
            image: '',
            footer: 'Chúc mừng bạn!'
          });
        } else if (correct) {
          setRewardData({
            title: 'Chính xác!',
            body1: 'Bạn đã trả lời đúng câu hỏi!',
            body2: 'Nhưng không thể cấp thêm điểm do lỗi hệ thống.',
            image: '',
            footer: 'Hãy thử lại sau!'
          });
        } else {
          setRewardData({
            title: 'Sai rồi!',
            body1: 'Câu trả lời của bạn không chính xác.',
            body2: 'Hãy thử lại lần sau nhé!',
            image: '',
            footer: 'Chúc bạn may mắn!'
          });
        }
        
        setShowRewardModal(true);
        setPendingReward(null);
        setCurrentQuizQuestion(null);
      }
    } catch (error) {
      console.error('Error answering mini quiz:', error);
      setShowMiniQuizModal(false);
      setRewardData({
        title: 'Lỗi!',
        body1: 'Không thể gửi câu trả lời.',
        body2: 'Vui lòng thử lại sau.',
        image: '',
        footer: 'Xin lỗi vì sự bất tiện!'
      });
      setShowRewardModal(true);
    }
  };

  // Use energy from local state (updated after upgrade) or user object
  const coins = currentEnergy ?? user?.energy ?? 0;
  const progressPercentage = (currentLevel / maxLevel) * 100;

  return (
    <div className="viewport-wrapper">
      <BaseLayout className="kiloba-hub-container" backgroundColor="transparent">
        {/* Header */}
        <div className='kiloba-hub-background'></div>
        
        <div className="kiloba-hub-header">
          <div className="back-button-kilohub" onClick={handleBack}>
            <img 
              src="/images/icons/back.svg" 
              alt="Back" 
              className="back-icon-svg"
            />
          </div>
          
          <div className="kiloba-hub-title">
            <span>KILO HUB</span>
          </div>
          <EnergyDisplay energy={coins} />
        </div>

        <div className="kiloba-hub-content">
          {/* Progress bar */}
          <div className="progress-container-kiloba">
            <div className="progress-bar-kiloba">
              <div 
                className="progress-fill" 
                style={{ width: `${progressPercentage}%` }}
              >
              </div>
              <div className="progress-text">
                  <span className="current-level">{currentLevel}</span>
                  <span className="separator">/</span>
                  <span className="max-level">{maxLevel}</span>
              </div>
            </div>
          </div>

          {/* Character display area */}
          <div className="character-display">
            {/* This will be the main Kiloba character */}
            <div className={`kiloba-character ${isUpgrading ? 'upgrading' : ''}`}>
              <img 
                src="/images/kilohub/main.png" 
                alt="Kiloba Character" 
                className="kiloba-character-image"
              />
            </div>
            
            {/* Multiple random animated arrows */}
            <div className="upgrade-animation">
              {[1, 2, 3, 4].map(index => (
                <AnimatedArrow key={index} index={index} />
              ))}
            </div>
          </div>
        </div>

        <div className='bottom-floor'>

        </div>

        {/* Upgrade button */}
        <div className="upgrade-section">
          <div 
            className={`upgrade-button ${loading ? 'loading' : ''}`}
            onClick={handleUpgrade}
            onMouseDown={() => setIsButtonPressed(true)}
            onMouseUp={() => setIsButtonPressed(false)}
            onMouseLeave={() => setIsButtonPressed(false)}
            onTouchStart={() => setIsButtonPressed(true)}
            onTouchEnd={() => setIsButtonPressed(false)}
            style={{
              backgroundImage: `url('${isButtonPressed ? '/images/btn_upgrade_down.png' : '/images/kilohub/upgrade.png'}')`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              pointerEvents: loading ? 'none' : 'auto'
            }}
          >
            {loading && <div className="loading-spinner">Đang thăng cấp...</div>}
          </div>
        </div>

        {/* Reward Modal */}
        <RewardModal
          isOpen={showRewardModal}
          onClose={() => setShowRewardModal(false)}
          title={rewardData.title}
          body1={rewardData.body1}
          body2={rewardData.body2}
          image={rewardData.image}
          footer={rewardData.footer}
        />

        {/* Gift Modal - for vouchers and mini quiz gifts */}
        <RewardModal
          isOpen={showGiftModal}
          onClose={() => setShowGiftModal(false)}
          title={rewardData.title}
          body1={rewardData.body1}
          body2={rewardData.body2}
          image={rewardData.image}
          footer={rewardData.footer}
          onImageClick={handleGiftClick}
        />

        {/* Mini Quiz Modal */}
        {currentQuizQuestion && (
          <MiniQuizModal
            isOpen={showMiniQuizModal}
            onClose={() => {
              setShowMiniQuizModal(false);
              setPendingReward(null);
              setCurrentQuizQuestion(null);
            }}
            question={currentQuizQuestion}
            onAnswer={handleMiniQuizAnswer}
          />
        )}
      </BaseLayout>
    </div>
  );
};

export default KilobaHubPage;
